/**
 * Dynamic Form Component
 *
 * Renders a complete form based on FormDefinition
 */

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { FormDefinition } from '../../api/types';
import { generateZodSchema, generateDefaultValues } from './schema-generator';
import { FieldRenderer } from './FieldRenderer';

interface DynamicFormProps {
  formDefinition: FormDefinition;
  onSubmit: (data: Record<string, unknown>) => void;
  isProcessing: boolean;
  submitButtonText?: string;
  processingText?: string;
}

export function DynamicForm({
  formDefinition,
  onSubmit,
  isProcessing,
  submitButtonText = 'Antrag verschlüsselt absenden',
  processingText = 'Wird verarbeitet...',
}: DynamicFormProps) {
  // Generate schema and defaults from form definition
  const schema = useMemo(() => generateZodSchema(formDefinition), [formDefinition]);
  const defaultValues = useMemo(() => generateDefaultValues(formDefinition), [formDefinition]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ mt: 2 }}>
        {formDefinition.fields.map((field) => (
          <FieldRenderer
            key={field.name}
            field={field}
            control={control}
            errors={errors}
            disabled={isProcessing}
          />
        ))}
      </Box>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!isValid || isProcessing}
        startIcon={isProcessing ? <CircularProgress size={20} /> : <SendIcon />}
        sx={{ mt: 2 }}
      >
        {isProcessing ? processingText : submitButtonText}
      </Button>
    </form>
  );
}
